import {
  GOLD_RATE_AED_PER_GRAM,
  GOLD_SELL_SPREAD,
  SILVER_RATE_AED_PER_GRAM,
  SILVER_SELL_SPREAD,
} from "@/lib/rates";

export interface LiveRates {
  goldBuy: number;
  goldSell: number;
  silverBuy: number;
  silverSell: number;
  goldChangePercent: number;
  silverChangePercent: number;
}

const FALLBACK: LiveRates = {
  goldBuy: GOLD_RATE_AED_PER_GRAM,
  goldSell: GOLD_RATE_AED_PER_GRAM - GOLD_SELL_SPREAD,
  silverBuy: SILVER_RATE_AED_PER_GRAM,
  silverSell: SILVER_RATE_AED_PER_GRAM - SILVER_SELL_SPREAD,
  goldChangePercent: 0,
  silverChangePercent: 0,
};

// Shared parsing for the upstream `{ price: {...} }` payload — used both by
// the server fetch below and by the client-side polling hook (useLivePrices)
// so every place on the site that shows a rate reads it the same way.
export function parseLiveRatesPayload(data: unknown): LiveRates | null {
  const p = (data as { price?: Record<string, unknown> } | null | undefined)
    ?.price;
  if (typeof p?.price24K !== "number" || typeof p?.silverPrice !== "number") {
    return null;
  }
  return {
    goldBuy: p.price24K,
    goldSell:
      typeof p.sellPrice24K === "number" ? p.sellPrice24K : p.price24K,
    silverBuy: p.silverPrice,
    silverSell:
      typeof p.sellPriceSilver === "number" ? p.sellPriceSilver : p.silverPrice,
    goldChangePercent:
      typeof p.changeGoldPercent === "number" ? p.changeGoldPercent : 0,
    silverChangePercent:
      typeof p.changeSilverPercent === "number" ? p.changeSilverPercent : 0,
  };
}

// Live gold/silver rates from the same upstream the /api/gold-price route
// proxies. Falls back to the static demo constants if the feed is
// unreachable, so a pricing API outage never breaks the build or the page.
export async function getLiveRates(): Promise<LiveRates> {
  try {
    const res = await fetch("https://api.prod.justgold.me/v1/prices/gold", {
      next: { revalidate: 60 },
    });
    if (!res.ok) throw new Error(`gold price API responded ${res.status}`);
    const parsed = parseLiveRatesPayload(await res.json());
    if (!parsed) throw new Error("unexpected gold price API shape");
    return parsed;
  } catch {
    return FALLBACK;
  }
}
